import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { Suspense, useState } from "react"
import { faShieldCheck } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    SERVICE_CENTER_ADMIN_ORGANIZATION_DEVICES_CREATE_ROUTE,
    SERVICE_CENTER_ADMIN_ROUTE,
    SUPPLIER_ADMIN_ROUTE,
} from "../../../../../../constants/routes"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { DeviceTable } from "../../../../../entities/devices/components/DeviceTable"
import { DeviceTableVariant, DeviceType } from "../../../../../entities/devices/deviceTypes"
import {
    DevicesServiceCenterResponse,
    fetchServiceCenterDevices,
    ServiceCenterDevicesQuery,
} from "../serviceCenterDevicesApi"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveServiceCenterId } from "../../../../../context/contextSelectors"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { SelectDeviceTypeFilter } from "../../../../../entities/devices/components/SelectDeviceTypeFilter"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { useExportDeviceToCsv } from "../components/ExportFunctionDevice"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { useNavigate } from "react-router-dom"

const selectDevices = (data: DevicesServiceCenterResponse) => data?.devices || []

function SupplierDevicesTable() {
    const serviceCenterId = useAppSelector(selectEffectiveServiceCenterId)
    const [type, setType] = useState<DeviceType>(DeviceType.DISPLAY)

    const devicesResult = useApiQuery(fetchServiceCenterDevices, {
        queryName: ServiceCenterDevicesQuery.DEVICES,
        dependencies: {
            serviceCenterId: serviceCenterId ?? 0,
            type,
        },
        enabled: Boolean(serviceCenterId),
        select: selectDevices,
    })
    const { ApiQueryTable } = useApiQueryTable(devicesResult)

    const exportDevices = useExportDeviceToCsv({
        data: devicesResult.data,
    })

    return (
        <DeviceTable
            variant={DeviceTableVariant.SERVICE_CENTER}
            ApiQueryTable={(props) => (
                <ApiQueryTable
                    {...props}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            <SelectDeviceTypeFilter deviceType={type} setDeviceType={setType} />
                        </StandardTableFilter>
                    )}
                    renderToolbarInternalActions={({ table }) => (
                        <StandardTableFilter variant="right" table={table}>
                            <StandardExportButton onClick={exportDevices} />
                        </StandardTableFilter>
                    )}
                />
            )}
        />
    )
}
export function ServiceCenterDevices() {
    const navigate = useNavigate()

    return (
        <>
            <LocalizedStrict id="service-center-admin-organization-devices-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SERVICE_CENTER_ADMIN_ORGANIZATION_DEVICES_ROUTE}
                    title="Devices"
                    rightElement={
                        <LocalizedStrict id="service-center-admin-organization-devices-button-title">
                            <StandardButton
                                type="submit"
                                onClick={() => navigate(SERVICE_CENTER_ADMIN_ORGANIZATION_DEVICES_CREATE_ROUTE)}
                            >
                                Create Device
                            </StandardButton>
                        </LocalizedStrict>
                    }
                    icon={<FontAwesomeIcon icon={faShieldCheck} />}
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={SERVICE_CENTER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-admin-nav-item-organization">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-admin-nav-item-organization-devices">
                            <Breadcrumb>Devices</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <SupplierDevicesTable />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
